import React from 'react';
import { Switch, Tooltip } from '@mui/material';
import { useDispatch } from 'react-redux';
import { showToast } from '@/features/toast/toastSlice';
import { useUpdateFeatureFlagMutation } from '../api/featureFlagsApi';
import { useAdminConfirm } from '../../shared/AdminConfirmProvider';
import { useFeatureFlagRules } from '../useFeatureFlagRules';

/**
 * Turns a feature flag on or off for everyone, after the admin confirms. Targeted users,
 * broker groups and ledgers keep the flag either way. Disabled for adminModeEnabled, which
 * can only be given to individual users.
 */
export const FeatureFlagActiveSwitch = ({ flag }) => {
	const dispatch = useDispatch();
	const confirm = useAdminConfirm();
	const { canEditActive } = useFeatureFlagRules(flag);
	const [updateFeatureFlag, { isLoading }] = useUpdateFeatureFlagMutation();

	const handleChange = async (event) => {
		const turnOn = event.target.checked;
		const confirmed = await confirm({
			title: `Turn ${flag.feature} ${turnOn ? 'on' : 'off'} for everyone?`,
			confirmText: turnOn ? 'Turn on' : 'Turn off',
		});
		if (!confirmed) return;

		const result = await updateFeatureFlag({ id: flag.id, active: turnOn });
		if (result.error) {
			dispatch(
				showToast({ message: `Couldn't update ${flag.feature}. Please try again.`, error: true }),
			);
		}
	};

	return (
		<Tooltip title={canEditActive ? '' : 'Admin mode can only be given to individual users'}>
			{/* The span keeps the tooltip working while the switch is disabled */}
			<span>
				<Switch
					checked={flag.active}
					onChange={handleChange}
					onClick={(event) => event.stopPropagation()}
					disabled={!canEditActive || isLoading}
					size='small'
					slotProps={{ input: { 'aria-label': `${flag.feature} on for everyone` } }}
				/>
			</span>
		</Tooltip>
	);
};
